<template lang="pug">
  .mt-three-object
    slot(v-if='ready')
</template>

<script>
import * as TWEEN from '@tweenjs/tween.js'
import requestSmoothAnimation from '@/utils/requestSmoothAnimation'
import { MAP_SCALE_INTERVAL, MAP_MOVE_INTERVAL, MAP_SCALE_SIZE } from '@/app.config'
import { MTLLoader } from 'three/examples/js/loaders/MTLLoader'
import { OBJLoader } from 'three/examples/js/loaders/OBJLoader'
import eventTunnel from '@/utils/eventTunnel'

const animate = () => {
  if (TWEEN.update()) {
    requestSmoothAnimation(animate)
  }
}

export default {
  props: {
    coordinates: {
      type: [Object, Array],
      required: true
    },
    heading: {
      type: Number,
      default: 0
    },
    color: {
      type: String,
      default: '#FFFF00'
    },
    isSelected: {
      type: Boolean,
      default: false
    },
    isSeen: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      mapObject: null,
      ready: false
    }
  },
  computed: {
    pathByColor () {
      return `/map-objects/bus-${this.color.replace('#', '')}/`
    }
  },
  mounted () {
    const scene = this.$parent.mapObject.getScene()

    const mtlLoader = new MTLLoader()
    mtlLoader.setTexturePath(this.pathByColor)
    mtlLoader.setPath(this.pathByColor)
    mtlLoader.load('bus.mtl', (materials) => {
      materials.preload()

      const objLoader = new OBJLoader()
      objLoader.setMaterials(materials)
      objLoader.setPath(this.pathByColor)
      objLoader.load('bus.obj', (object) => {
        const v = this.$parent.mapObject.coordinateToVector3(this.coordinates)
        object.position.x = v.x
        object.position.y = v.y

        object.rotation.z = this.heading * (Math.PI / 180)

        object.scale.x *= 1.5
        object.scale.y *= 1.5
        object.scale.z *= 1.5

        scene.add(object)
        this.$parent.mapObject.redraw()

        this.mapObject = object
        this.ready = true

        this.$emit('loaded')
      })
    })
  },
  watch: {
    isSeen (newValue) {
      if (!this.mapObject) return

      this.mapObject.visible = newValue
    },
    isSelected (newValue) {
      if (!this.mapObject) return

      const callScale = () => {
        const newScale = newValue
          ? { x: MAP_SCALE_SIZE, y: MAP_SCALE_SIZE, z: MAP_SCALE_SIZE }
          : { x: 1, y: 1, z: 1 }

        const oldScale = newValue
          ? { x: 1, y: 1, z: 1 }
          : { x: MAP_SCALE_SIZE, y: MAP_SCALE_SIZE, z: MAP_SCALE_SIZE }

        this.scale({
          newScale,
          oldScale
        })
      }

      if (newValue) {
        eventTunnel.$emit('map_animateTo', {
          center: this.coordinates,
          cb: callScale
        })
      } else {
        callScale()
      }
    },
    coordinates (newValue, oldValue) {
      if (!this.mapObject) return

      let newRotation = this.heading * (Math.PI / 180)
      const oldRotation = this.mapObject.rotation.z

      if (Math.abs(oldRotation - newRotation) > Math.PI) {
        newRotation = newRotation - 2 * Math.PI
      }

      this.move({
        newPosition: newValue,
        oldPosition: oldValue,
        newRotation,
        oldRotation
      })
    }
  },
  methods: {
    scale ({ newScale, oldScale }) {
      new TWEEN.Tween(oldScale)
        .to(newScale, MAP_SCALE_INTERVAL)
        .easing(TWEEN.Easing.Quadratic.InOut)
        .onUpdate(({ x, y, z }) => {
          this.mapObject.scale.x = x
          this.mapObject.scale.y = y
          this.mapObject.scale.z = z
          this.$parent.mapObject.redraw()
        })
        .start()

      animate()
    },
    move ({ newPosition, oldPosition, newRotation, oldRotation }) {
      const from = {
        ...this.$parent.mapObject.coordinateToVector3(oldPosition),
        rotation: oldRotation
      }
      const to = {
        ...this.$parent.mapObject.coordinateToVector3(newPosition),
        rotation: newRotation
      }
      new TWEEN.Tween(from)
        .to(to, MAP_MOVE_INTERVAL)
        .easing(TWEEN.Easing.Quadratic.InOut)
        .onUpdate(({ x, y, rotation }) => {
          this.mapObject.position.x = x
          this.mapObject.position.y = y
          this.mapObject.rotation.z = rotation
          this.$parent.mapObject.redraw()
        })
        .start()

      animate()
    }
  },
  beforeDestroy () {
  }
}
</script>

<style scoped>
.mt-three-object {
  display: none;
}
</style>
